/**
 * Where the old CRM is, and whether we can currently read it.
 *
 * The first card on the tab because everything under it is meaningless without
 * this answer: an entity table full of zeroes and a connection nobody checked
 * look identical, and only one of them means "there is nothing to move".
 *
 * NO CREDENTIAL IS EVER DRAWN HERE. The server sends a host and a database and
 * nothing else, and the sentence about it is on screen rather than only in this
 * comment - a screen that shows a database host is a screen people screenshot.
 */
import { Loader2, Plug, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { useT } from "../../../../i18n/I18nProvider";
import type { ImportStatus, Introspection } from "../../../../lib/crmImport";
import { Btn, Card, CardHead, StatusPill, fmtNum } from "../../../ui";

/** One labelled fact. A definition list rather than a table: four rows of two
 *  cells do not need column rules to be read. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="crm-lab !mt-0">{label}</span>
      <span className="min-w-0 break-words text-[13px] font-semibold">{value}</span>
    </div>
  );
}

export function ConnectionCard({
  status,
  intro,
  busy,
  onRefresh,
}: {
  status: ImportStatus;
  /** Null when the schema read failed or has not run - the card still draws. */
  intro: Introspection | null;
  busy: boolean;
  onRefresh: () => void;
}) {
  const t = useT();
  const connection = status.connection;
  const live = connection?.ok === true;

  return (
    <Card>
      <CardHead
        title={t("crmImport.connection.title")}
        sub={t("crmImport.connection.desc")}
        actions={
          <div className="flex items-center gap-2.5">
            <StatusPill tone={!status.configured ? "mut" : live ? "ok" : "lt"}>
              {t(
                !status.configured
                  ? "crmImport.connection.notConfigured"
                  : live
                    ? "crmImport.connection.ok"
                    : "crmImport.connection.failed",
              )}
            </StatusPill>
            <Btn small onClick={onRefresh} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
              {t("crmImport.refresh")}
            </Btn>
          </div>
        }
      />

      <div className="px-6 pb-6 pt-1">
        {!status.configured ? (
          <div className="crm-note">
            <div className="crm-note-t">
              <Plug className="size-4" />
              {t("crmImport.connection.missingTitle")}
            </div>
            <p className="crm-note-p">{t("crmImport.connection.missing")}</p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Fact label={t("crmImport.connection.host")} value={status.target?.host ?? "-"} />
              <Fact label={t("crmImport.connection.database")} value={status.target?.database ?? "-"} />
              <Fact
                label={t("crmImport.connection.version")}
                value={connection?.ok ? connection.version : "-"}
              />
              <Fact
                label={t("crmImport.connection.tableCount")}
                value={intro ? fmtNum(intro.tableCount) : "-"}
              />
            </div>

            {connection && !connection.ok ? (
              // The server's own words, not a phrasing lookup: a connection
              // refusal is usually a tunnel that is down or a host that moved,
              // and a friendly sentence in place of the real message would cost
              // whoever has to fix it the one clue there is.
              <p className="crm-sub mt-4 break-words text-[12.5px] text-destructive">{connection.error}</p>
            ) : null}

            <p className="crm-sub mt-4 text-[11.5px] leading-snug">{t("crmImport.connection.noSecrets")}</p>
          </>
        )}
      </div>
    </Card>
  );
}
